import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Skeleton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Pagination,
  IconButton,
  Tooltip,
  MenuItem,
  CircularProgress
} from "@mui/material";
import { getData, postData } from "../../Webservice/Webservice";
import { MdSystemUpdateAlt, MdHistory } from "react-icons/md";
import { FiFilter as FilterIcon } from "react-icons/fi";
import { Drawer } from "@mui/material";

const PAGE_SIZE = 20;
const initialFormData = {
  supplierId: "",
  receivingId: "",
  amount: "",
  note: "",
};

function SupplierPayout() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [openHistoryModal, setOpenHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [receivings, setReceivings] = useState([]);
  const [receivingsLoading, setReceivingsLoading] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    supplierName: "",
    phone: "",
    email: ""
  });
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    supplierName: "",
    phone: "",
    email: ""
  });

  const totalPages = Math.max(1, Math.ceil(suppliers.length / PAGE_SIZE));
  const paginatedSuppliers = suppliers.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "0";
    const num = Number(value);
    if (Number.isNaN(num)) return "0";
    return `₹${num.toLocaleString()}`;
  };

  const fetchSupplierPayouts = async () => {
    setLoading(true);
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const params = {
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""))
      };
      const response = await getData(
        "supplier/payment/all",
        params,
        token
      );
      if (response && response.success && Array.isArray(response.data)) {
        setSuppliers(response.data);
      } else {
        setSuppliers([]);
      }
    } catch (error) {
      console.error("Error fetching supplier payouts:", error);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupplierPayouts();
  }, [filters]);

  useEffect(() => {
    if (page > totalPages && totalPages >= 1) setPage(1);
  }, [suppliers.length, page, totalPages]);

  const handleOpenUpdateModal = (supplier) => {
    setFormData({
      supplierId: supplier?.supplierId ?? "",
      receivingId: "",
      amount: "",
      note: "",
    });
    if (supplier?.supplierId) {
      fetchReceivings(supplier.supplierId);
    }
    setOpenModal(true);
  };

  const fetchReceivings = async (supplierId) => {
    setReceivingsLoading(true);
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const response = await getData(`supplier/receiving/${supplierId}`, undefined, token);
      if (response && response.success && Array.isArray(response.data)) {
        setReceivings(response.data);
      } else {
        setReceivings([]);
      }
    } catch (error) {
      console.error("Error fetching receivings:", error);
      setReceivings([]);
    } finally {
      setReceivingsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData(initialFormData);
    setReceivings([]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleSubmit = async () => {
    if (!formData.supplierId?.trim()) return;
    setSubmitting(true);
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      await postData(
        "supplier/payment-add",
        {
          supplierId: formData.supplierId,
          receivingId: formData.receivingId,
          amount: formData.amount,
          note: formData.note,
        },
        token
      );
      handleCloseModal();
      fetchSupplierPayouts();
    } catch (err) {
      console.error("Update payout error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const fetchPayoutHistory = async (supplierId) => {
    setHistoryLoading(true);
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const response = await getData(
        `supplier/payment-list/${supplierId}`,
        undefined,
        token
      );
      setHistoryData(response);
    } catch (error) {
      console.error("Error fetching payout history:", error);
      setHistoryData(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistoryModal = (supplier) => {
    fetchPayoutHistory(supplier.supplierId);
    setOpenHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setOpenHistoryModal(false);
    setHistoryData(null);
  };

  return (
    <Box p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Typography variant="h3" style={{ fontFamily: "Mulish, sans-serif" }}>
            Supplier Payout
          </Typography>
          <Chip
            label={`${suppliers.length} ${suppliers.length === 1
              ? "Item"
              : "Items"}`}
            size="small"
            sx={{
              backgroundColor: "rgba(255, 209, 2, 0.2)",
              color: "#b39200",
              fontFamily: "Mulish, sans-serif",
              fontWeight: "800",
              border: "1px solid rgba(255, 209, 2, 0.5)"
            }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: "12px" }}>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => {
              setTempFilters(filters);
              setFilterDrawerOpen(true);
            }}
            sx={{
              fontFamily: 'Mulish, sans-serif',
              borderRadius: '10px',
              color: '#4b5563',
              borderColor: '#e5e7eb',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              px: 2,
              '&:hover': {
                borderColor: '#d1d5db',
                backgroundColor: '#f9fafb'
              }
            }}
          >
            Filters
          </Button>
        </Box>
      </Box>

      {/* Filter Drawer UI */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <Box sx={{ width: 350, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontFamily: 'Mulish, sans-serif', fontWeight: 700 }}>
            Filters
          </Typography>

          <TextField
            fullWidth
            variant="outlined"
            label="Supplier Name"
            size="small"
            value={tempFilters.supplierName}
            onChange={e => setTempFilters({ ...tempFilters, supplierName: e.target.value })}
            sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish, sans-serif" } }}
          />

          <TextField
            fullWidth
            variant="outlined"
            label="Phone"
            size="small"
            value={tempFilters.phone}
            onChange={e => setTempFilters({ ...tempFilters, phone: e.target.value })}
            sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish, sans-serif" } }}
          />

          <TextField
            fullWidth
            variant="outlined"
            label="Email"
            size="small"
            value={tempFilters.email}
            onChange={e => setTempFilters({ ...tempFilters, email: e.target.value })}
            sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish, sans-serif" } }}
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                const cleared = { supplierName: "", phone: "", email: "" };
                setTempFilters(cleared);
                setFilters(cleared);
                setPage(1);
                setFilterDrawerOpen(false);
              }}
              sx={{
                fontFamily: 'Mulish, sans-serif',
                borderRadius: '10px',
                color: '#4b5563',
                borderColor: '#e5e7eb',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { borderColor: '#d1d5db', backgroundColor: '#f9fafb' }
              }}
            >
              Clear All
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setFilters(tempFilters);
                setPage(1);
                setFilterDrawerOpen(false);
              }}
              sx={{
                fontFamily: 'Mulish, sans-serif',
                borderRadius: '10px',
                background: 'rgb(255 209 2)',
                color: 'black',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
                border: '1px solid black',
                '&:hover': {
                  background: 'rgb(255 209 2)',
                  boxShadow: '0 6px 16px rgba(255, 209, 2, 0.4)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Drawer>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          border: "1px solid #d3d3d3de"
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  backgroundColor: "#f8f9fa",
                  fontWeight: 800,
                  py: 2,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  color: "#5c636a",
                  fontFamily: "Mulish, sans-serif",
                  borderBottom: "2px solid #edeff1"
                }
              }}
            >
              <TableCell>Supplier Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Total Invoice Amount</TableCell>
              <TableCell>Total Return Amount</TableCell>
              <TableCell>Total Paid Amount</TableCell>
              <TableCell>Total Pending Amount</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({
                  length: 5
                }).map((_, idx) =>
                  <TableRow key={idx}>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                    <TableCell>
                      <Skeleton />
                    </TableCell>
                  </TableRow>
                )
              : suppliers.length > 0
                ? paginatedSuppliers.map((supplier, index) =>
                    <TableRow
                      key={supplier.supplierId || index}
                      hover
                      sx={{
                        "&:hover": { backgroundColor: "#f9fafb" },
                        transition: "background-color 0.2s ease"
                      }}
                    >
                      <TableCell>
                        <Typography
                          fontWeight={700}
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            fontSize: "0.95rem",
                            color: "#1a1c1e"
                          }}
                        >
                          {supplier.supplierName || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          fontWeight={600}
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            color: "#4b5563",
                            fontSize: "0.9rem"
                          }}
                        >
                          {supplier.phone || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          fontWeight={600}
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            color: "#4b5563",
                            fontSize: "0.9rem"
                          }}
                        >
                          {supplier.email || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          fontWeight={700}
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            color: "#4b5563",
                            fontSize: "0.95rem"
                          }}
                        >
                          {formatCurrency(supplier.totalInvoiceAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          fontWeight={700}
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            color: "#4b5563",
                            fontSize: "0.95rem"
                          }}
                        >
                          {formatCurrency(supplier.totalReturnAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          fontWeight={700}
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            color: "#4b5563",
                            fontSize: "0.95rem"
                          }}
                        >
                          {formatCurrency(supplier.totalPaidAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          fontWeight={700}
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            color:
                              supplier.totalPendingAmount === null ||
                              supplier.totalPendingAmount === undefined
                                ? "#6b7280"
                                : "#059669",
                            fontSize: "0.95rem"
                          }}
                        >
                          {formatCurrency(supplier.totalPendingAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Button
                            fontWeight={700}
                            startIcon={<MdSystemUpdateAlt />}
                            onClick={() => handleOpenUpdateModal(supplier)}
                            sx={{
                              fontFamily: "Mulish, sans-serif",
                              color: "#966f05",
                              textTransform: "none"
                            }}
                          >
                            Update
                          </Button>
                          <Tooltip title="View History">
                            <IconButton
                              fontWeight={700}
                              onClick={() => handleOpenHistoryModal(supplier)}
                              sx={{
                                fontFamily: "Mulish, sans-serif",
                                color: "#121a18ff",
                                textTransform: "none"
                              }}
                            >
                              <MdHistory fontSize={20}/>
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                : <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 2
                        }}
                      >
                        <img
                          src="/img/nodata.png"
                          alt="No data"
                          style={{ width: "180px" }}
                        />
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            color: "text.secondary"
                          }}
                        >
                          No payout data available
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      {suppliers.length > 0 && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
              "& .MuiPagination-ul": { flexWrap: "nowrap" },
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              sx={{
                "& .MuiPaginationItem-root": {
                  fontFamily: "Mulish, sans-serif",
                  fontWeight: 600,
                  borderRadius: "8px",
                  "&.Mui-selected": {
                    backgroundColor: "rgb(255, 209, 2)",
                    color: "black",
                    "&:hover": {
                      backgroundColor: "rgb(255, 190, 0)",
                    },
                  },
                },
              }}
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mt: 1, display: "block", textAlign: "center" }}
          >
            Showing {(page - 1) * PAGE_SIZE + 1} to{" "}
            {Math.min(page * PAGE_SIZE, suppliers.length)} of {suppliers.length}{" "}
            entries
          </Typography>
        </>
      )}

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Mulish, sans-serif",
            fontWeight: 700,
            color: "#1a1c1e",
            borderBottom: "1px solid #edeff1",
            pb: 1.5,
          }}
        >
          Update Payout
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            name="supplierId"
            label="Supplier ID"
            value={formData.supplierId}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
            disabled
            sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish, sans-serif" } }}
          />
          <TextField
            select
            name="receivingId"
            label="Select Invoice (Receiving)"
            value={formData.receivingId}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
            disabled={receivingsLoading}
            InputProps={{
              endAdornment: receivingsLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : null,
            }}
            sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish, sans-serif" } }}
          >
            {receivings.length > 0 ? (
              receivings.map((option) => (
                <MenuItem 
                  key={option._id} 
                  value={option._id}
                  sx={{ fontFamily: "Mulish, sans-serif" }}
                >
                  {option.invoiceNumber} (Pending: ₹{option.pendingAmount})
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled sx={{ fontFamily: "Mulish, sans-serif" }}>
                {receivingsLoading ? "Loading..." : "No invoices found"}
              </MenuItem>
            )}
          </TextField>
          <TextField
            name="amount"
            label="Amount"
            type="number"
            value={formData.amount}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
            placeholder="Enter Amount"
            sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish, sans-serif" } }}
          />
          <TextField
            name="note"
            label="Note"
            value={formData.note}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
            multiline
            rows={2}
            placeholder="Enter Note"
            sx={{ "& .MuiInputBase-root": { fontFamily: "Mulish, sans-serif" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
          <Button
            onClick={handleCloseModal}
            sx={{
              fontFamily: "Mulish, sans-serif",
              textTransform: "none",
              color: "#6b7280",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !formData.supplierId?.trim()}
            sx={{
              fontFamily: "Mulish, sans-serif",
              textTransform: "none",
              backgroundColor: "#ffd102",
              color: "black",
              "&:hover": { backgroundColor: "#ffd102" },
            }}
          >
            {submitting ? "Saving..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openHistoryModal}
        onClose={handleCloseHistoryModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Mulish, sans-serif",
            fontWeight: 700,
            color: "#1a1c1e",
            borderBottom: "1px solid #edeff1",
            pb: 1.5,
          }}
        >
          Payout History - {historyData?.supplierName || "Supplier"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Skeleton variant="rectangular" width="100%" height={200} />
            </Box>
          ) : historyData?.data?.length > 0 ? (
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #d3d3d3de" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        backgroundColor: "#f8f9fa",
                        fontWeight: 700,
                        py: 1.5,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "#5c636a",
                        fontFamily: "Mulish, sans-serif",
                        borderBottom: "2px solid #edeff1"
                      }
                    }}
                  >
                    <TableCell>Date</TableCell>
                    <TableCell>Receiving ID</TableCell>
                    <TableCell>Note</TableCell>
                    {/* <TableCell>Debit</TableCell> */}
                    <TableCell>Credit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyData.data.map((payout) => (
                    <TableRow key={payout._id} hover sx={{ "&:hover": { backgroundColor: "#f9fafb" } }}>
                      <TableCell>
                        <Typography
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            color: "#4b5563",
                            fontSize: "0.85rem"
                          }}
                        >
                          {new Date(payout.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            color: "#4b5563",
                            fontSize: "0.85rem"
                          }}
                        >
                          {payout.receivingId || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            color: "#4b5563",
                            fontSize: "0.85rem"
                          }}
                        >
                          {payout.note || "N/A"}
                        </Typography>
                      </TableCell>
                      {/* <TableCell>
                        <Typography
                          fontWeight={600}
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            color: payout.debit > 0 ? "#dc2626" : "#4b5563",
                            fontSize: "0.9rem"
                          }}
                        >
                          {formatCurrency(payout.debit)}
                        </Typography>
                      </TableCell> */}
                      <TableCell>
                        <Typography
                          fontWeight={600}
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            color: payout.credit > 0 ? "#059669" : "#4b5563",
                            fontSize: "0.9rem"
                          }}
                        >
                          {formatCurrency(payout.credit)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={4}
              gap={2}
            >
              <img
                src="/img/nodata.png"
                alt="No data"
                style={{ width: "120px" }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Mulish, sans-serif",
                  color: "text.secondary"
                }}
              >
                No payout history available
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
          <Button
            onClick={handleCloseHistoryModal}
            sx={{
              fontFamily: "Mulish, sans-serif",
              textTransform: "none",
              backgroundColor: "#ffd102",
              color: "black",
              "&:hover": { backgroundColor: "#ffd102" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SupplierPayout;
